import { create } from 'zustand';
import type { WalkthroughStep } from '../../store/walkthroughStore';

export type TourPrepStatus = 'idle' | 'preparing' | 'ready' | 'error';

export type TourBannerPhase = 'sent' | 'ready' | null;

type PathState = {
  status: TourPrepStatus;
  passos?: WalkthroughStep[];
  pageResenha?: string | null;
  fonte?: string;
  updatedAt: number;
};

type BannerState = {
  phase: TourBannerPhase;
  path: string;
  pageTitle?: string;
  message: string;
  visible: boolean;
};

type TourPrepStore = {
  byPath: Record<string, PathState>;
  banner: BannerState;
  get: (path: string) => PathState | undefined;
  setPreparing: (path: string) => void;
  setReady: (
    path: string,
    data: {
      passos: WalkthroughStep[];
      pageResenha?: string | null;
      fonte?: string;
    },
  ) => void;
  setError: (path: string) => void;
  showBannerSent: (path: string, pageTitle?: string) => void;
  showBannerReady: (path: string, pageTitle?: string) => void;
  hideBanner: () => void;
};

const emptyBanner: BannerState = {
  phase: null,
  path: '',
  message: '',
  visible: false,
};

export const useTourPrepStore = create<TourPrepStore>((set, get) => ({
  byPath: {},
  banner: emptyBanner,
  get: (path) => get().byPath[path],
  setPreparing: (path) =>
    set((s) => ({
      byPath: {
        ...s.byPath,
        [path]: {
          ...(s.byPath[path] || { updatedAt: 0 }),
          status: 'preparing',
          updatedAt: Date.now(),
        },
      },
    })),
  setReady: (path, data) =>
    set((s) => ({
      byPath: {
        ...s.byPath,
        [path]: {
          status: 'ready',
          passos: data.passos,
          pageResenha: data.pageResenha,
          fonte: data.fonte,
          updatedAt: Date.now(),
        },
      },
    })),
  setError: (path) =>
    set((s) => ({
      byPath: {
        ...s.byPath,
        [path]: { status: 'error', updatedAt: Date.now() },
      },
    })),
  showBannerSent: (path, pageTitle) =>
    set({
      banner: {
        phase: 'sent',
        path,
        pageTitle,
        visible: true,
        message:
          'Tour enviado com sucesso. O servidor está processando — pode trocar de página.',
      },
    }),
  showBannerReady: (path, pageTitle) =>
    set({
      banner: {
        phase: 'ready',
        path,
        pageTitle,
        visible: true,
        message: pageTitle
          ? `Tour de “${pageTitle}” pronto. O ? no topo indica disponibilidade.`
          : 'Tour pronto. O ? no topo indica disponibilidade.',
      },
    }),
  hideBanner: () => set({ banner: emptyBanner }),
}));
